import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import './styles/app.css'
import { ApiError } from './lib/api'
import { AuthProvider, useAuth } from './lib/auth'
import Shell from './components/Shell'
import Login from './pages/Login'
import Dashboard from './pages/Dashboard'
import Devices from './pages/Devices'
import DeviceDetail from './pages/DeviceDetail'
import DeviceGroups from './pages/DeviceGroups'
import Sources from './pages/Sources'
import AutoReplies from './pages/AutoReplies'
import Messages from './pages/Messages'
import Users from './pages/Users'
import Settings from './pages/Settings'
import { Alerts, Audit, Inbound, Reports } from './pages/Simple'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 3_000,
      refetchOnWindowFocus: true,
      // A 401 means the session ended; retrying just delays showing the login screen. Everything
      // else gets one retry to ride out a blip.
      retry: (failureCount, error) =>
        error instanceof ApiError && error.isUnauthorized ? false : failureCount < 1,
    },
  },
})

function Gate() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="login-wrap">
        <div className="faint">Loading…</div>
      </div>
    )
  }

  if (!user) return <Login />

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Dashboard />} />
        <Route path="messages" element={<Messages />} />
        <Route path="inbound" element={<Inbound />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="devices" element={<Devices />} />
        <Route path="devices/:id" element={<DeviceDetail />} />
        <Route path="groups" element={<DeviceGroups />} />
        <Route path="sources" element={<Sources />} />
        <Route path="auto-replies" element={<AutoReplies />} />
        <Route path="settings" element={<Settings />} />
        <Route path="users" element={<Users />} />
        <Route path="audit" element={<Audit />} />
        <Route path="reports" element={<Reports />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      {/* The app is served under /admin by ASP.NET, so routes are relative to that. */}
      <BrowserRouter basename="/admin">
        <AuthProvider>
          <Gate />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
)
